import axios from 'axios';
import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Trophy, TrendingUp, TrendingDown, Users, DollarSign, Clock,
  RefreshCw, ChevronDown, ChevronUp, Award, Star, Crown, Gem,
  ArrowLeftRight, Lock, Info, CheckCircle2
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { usePermissions } from "@/lib/PermissionsContext";

const PALIER_CONFIG = {
  Bronze: { icon: Award, color: "text-amber-600", bg: "bg-amber-50 border-amber-200" },
  Argent: { icon: Star, color: "text-slate-500", bg: "bg-slate-50 border-slate-200" },
  Or: { icon: Crown, color: "text-yellow-500", bg: "bg-yellow-50 border-yellow-200" },
  Platine: { icon: Gem, color: "text-blue-500", bg: "bg-blue-50 border-blue-200" },
  Aucun: { icon: TrendingDown, color: "text-red-500", bg: "bg-red-50 border-red-200" },
};

const formatCAD = (n) => new Intl.NumberFormat('fr-CA', { style: 'currency', currency: 'CAD', maximumFractionDigits: 0 }).format(n || 0);

// 🆕 [Phase 55] Badge Delta pour onglet Comparaison pondérée
const DeltaBadge = ({ delta }) => {
  const significatif = Math.abs(delta) > 50;
  const positif = delta > 0;
  if (Math.abs(delta) < 0.01) {
    return (
      <Badge variant="secondary" data-testid="audit-bonus-delta-neutre" className="text-slate-500 bg-slate-100">
        = 0
      </Badge>
    );
  }
  const Icon = positif ? TrendingUp : TrendingDown;
  const cls = positif
    ? significatif ? 'text-green-700 bg-green-100 border-green-300' : 'text-green-600 bg-green-50'
    : significatif ? 'text-red-700 bg-red-100 border-red-300' : 'text-red-600 bg-red-50';
  return (
    <Badge data-testid={`audit-bonus-delta-${positif ? 'positif' : 'negatif'}`} className={`${cls} border font-semibold gap-1`}>
      <Icon className="w-3.5 h-3.5" />
      {positif ? '+' : ''}{formatCAD(delta)}
    </Badge>
  );
};

export const GestionBonus = () => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [comparaisonData, setComparaisonData] = useState(null);  // 🆕 [Phase 55] Comparaison pondérée
  const [comparaisonLoading, setComparaisonLoading] = useState(false);
  const [statutPondere, setStatutPondere] = useState(null);  // 🆕 [Phase 55 FINAL] {active, activated_at, activated_by}
  const [saisons, setSaisons] = useState([]);
  const [saison, setSaison] = useState("");
  const [loading, setLoading] = useState(true);
  const [expandedTeam, setExpandedTeam] = useState(null);
  const [activeTab, setActiveTab] = useState("resultats");

  // 🆕 [Phase v13.61 Vague 5b — 2026-08-25] Branchement RBAC via `bonus_comparaison_ponderee`.
  // Contrôle : onglet "Comparaison pondérée" (Owner-only par défaut).
  // API `/api/bonus/comparaison-pondere` déjà lockdown Owner-only (Vague 1).
  const { canDo } = usePermissions() || {};
  const isProprio = !!(canDo && canDo('bonus_comparaison_ponderee'));

  const loadSaisons = useCallback(async () => {
    try {
      const res = await axios.get(`${api.baseUrl}/bonus/saisons`);
      const d = res.data;
      setSaisons(d.saisons || []);
      setSaison(d.courante || "");
    } catch (e) {
      console.error(e);
    }
  }, []);

  const loadBonus = useCallback(async (s) => {
    if (!s) return;
    setLoading(true);
    try {
      const res = await axios.get(`${api.baseUrl}/bonus/calcul?saison=${encodeURIComponent(s)}`);
      const d = res.data;
      setData(d);
    } catch (e) {
      toast.error("Erreur chargement bonus");
    } finally {
      setLoading(false);
    }
  }, []);

  // 🆕 [Phase 55] Charger la comparaison pondérée (Owner only)
  const loadComparaison = useCallback(async (s) => {
    if (!s || !isProprio) return;
    setComparaisonLoading(true);
    try {
      const res = await axios.get(`${api.baseUrl}/bonus/comparaison-pondere?saison=${encodeURIComponent(s)}`);
      setComparaisonData(res.data);
    } catch (e) {
      if (e.response?.status === 403) {
        // Silencieux : pas owner, l'onglet ne sera pas visible de toute façon
      } else {
        toast.error("Erreur chargement comparaison pondérée");
      }
      setComparaisonData(null);
    } finally {
      setComparaisonLoading(false);
    }
  }, [isProprio]);

  // 🆕 [Phase 55 FINAL] Charger l'état du flag bonus_v2_pondere (Owner only)
  const loadStatutPondere = useCallback(async () => {
    if (!isProprio) return;
    try {
      const res = await axios.get(`${api.baseUrl}/bonus/statut-pondere`);
      setStatutPondere(res.data);
    } catch (e) {
      // Silencieux si non-owner (403)
      setStatutPondere(null);
    }
  }, [isProprio]);

  useEffect(() => { loadSaisons(); }, [loadSaisons]);
  useEffect(() => { loadStatutPondere(); }, [loadStatutPondere]);
  useEffect(() => { if (saison) loadBonus(saison); }, [saison, loadBonus]);
  // 🆕 [Phase 55] Charger la comparaison quand l'onglet est ouvert ou la saison change
  useEffect(() => {
    if (saison && activeTab === "comparaison" && isProprio) loadComparaison(saison);
  }, [saison, activeTab, isProprio, loadComparaison]);

  const PalierBadge = ({ niveau }) => {
    const cfg = PALIER_CONFIG[niveau] || PALIER_CONFIG.Aucun;
    const Icon = cfg.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-sm font-semibold border ${cfg.bg} ${cfg.color}`}>
        <Icon className="w-4 h-4" />{niveau}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">Calcul des Bonus</h1>
          <p className="text-slate-500 mt-1">Bonus saisonniers basés sur les heures économisées par équipe</p>
        </div>
        <div className="flex gap-2 items-center">
          <Select value={saison} onValueChange={setSaison}>
            <SelectTrigger className="w-44">
              <SelectValue placeholder="Saison..." />
            </SelectTrigger>
            <SelectContent>
              {saisons.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={() => loadBonus(saison)}><RefreshCw className="w-4 h-4" /></Button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12 text-slate-500">Calcul en cours...</div>
      ) : !data ? null : (
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList data-testid="gestion-bonus-tabs">
            <TabsTrigger value="resultats" data-testid="tab-resultats">Résultats par équipe</TabsTrigger>
            {isProprio && (
              <TabsTrigger value="comparaison" data-testid="tab-comparaison" className="gap-2">
                <ArrowLeftRight className="w-4 h-4" />
                Comparaison pondérée
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="resultats" className="space-y-6 mt-0">
          {/* Summary cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
              <CardContent className="pt-5">
                <div className="text-sm text-blue-600 font-medium mb-1">Bonus CP (Chargé de Projet)</div>
                <div className="text-3xl font-bold text-blue-800">{formatCAD(data.bonus_cp)}</div>
                <div className="text-xs text-blue-500 mt-1">
                  Valeur CP totale: {formatCAD(data.valeur_cp_total)}
                  {data.palier_cp && <span className="ml-2">— Palier: {data.palier_cp.niveau}</span>}
                </div>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-emerald-50 to-teal-50 border-emerald-200">
              <CardContent className="pt-5">
                <div className="text-sm text-emerald-600 font-medium mb-1">Équipes actives</div>
                <div className="text-3xl font-bold text-emerald-800">{data.equipes?.length || 0}</div>
                <div className="text-xs text-emerald-500 mt-1">Saison: {data.saison}</div>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200">
              <CardContent className="pt-5">
                <div className="text-sm text-amber-600 font-medium mb-1">Moy. heures économisées</div>
                <div className="text-3xl font-bold text-amber-800">{(data.moyenne_heures_eco || 0).toFixed(2).replace('.', ',')}h</div>
                <div className="text-xs text-amber-500 mt-1">Par équipe</div>
              </CardContent>
            </Card>
          </div>

          {/* Équipes */}
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-slate-800">Résultats par équipe</h2>
            {(data.equipes || []).map(eq => {
              const isExpanded = expandedTeam === eq.chef_equipe_id;
              const palierCfg = PALIER_CONFIG[eq.palier?.niveau] || PALIER_CONFIG.Aucun;
              return (
                <Card key={eq.chef_equipe_id} className={`border-l-4 ${eq.heures_economisees > 0 ? 'border-l-emerald-400' : 'border-l-red-400'}`}>
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-slate-700 text-white flex items-center justify-center font-bold text-sm">{eq.classement}</div>
                        <div>
                          <div className="font-semibold text-slate-900">{eq.chef_equipe_nom || "Équipe sans nom"}</div>
                          <div className="text-xs text-slate-500">{eq.membres?.length || 0} membre(s)</div>
                        </div>
                        <PalierBadge niveau={eq.palier?.niveau || "Aucun"} />
                      </div>

                      <div className="flex items-center gap-6 flex-wrap">
                        <div className="text-center">
                          <div className={`text-lg font-bold ${eq.heures_economisees > 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                            {eq.heures_economisees > 0 ? '+' : ''}{eq.heures_economisees?.toFixed(2).replace('.', ',')}h
                          </div>
                          <div className="text-xs text-slate-400">Économisées</div>
                        </div>
                        <div className="text-center">
                          <div className="text-lg font-bold text-slate-700">{formatCAD(eq.valeur_economisee)}</div>
                          <div className="text-xs text-slate-400">Valeur</div>
                        </div>
                        <div className="text-center">
                          <div className="text-lg font-bold text-blue-700">{formatCAD(eq.pool_total)}</div>
                          <div className="text-xs text-slate-400">Pool total</div>
                        </div>
                        <div className="text-center">
                          <div className="text-lg font-bold text-purple-700">{formatCAD(eq.bonus_chef_brut)}</div>
                          <div className="text-xs text-slate-400">Bonus chef</div>
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => setExpandedTeam(isExpanded ? null : eq.chef_equipe_id)}>
                          {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </Button>
                      </div>
                    </div>

                    {/* Progress bar palier */}
                    {eq.palier?.niveau !== "Aucun" && eq.palier?.progression !== undefined && (
                      <div className="mt-3">
                        <div className="flex justify-between text-xs text-slate-500 mb-1">
                          <span>Progression palier {eq.palier.niveau}</span>
                          <span>{eq.palier.progression?.toFixed(0)}%</span>
                        </div>
                        <Progress value={eq.palier.progression} className="h-2" />
                      </div>
                    )}

                    {/* Détails équipe */}
                    {isExpanded && (
                      <div className="mt-4 pt-4 border-t border-slate-100">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4 text-sm">
                          <div><span className="text-slate-500">Budgetées:</span> <strong>{eq.heures_budgetees?.toFixed(2).replace('.', ',')}h</strong></div>
                          <div><span className="text-slate-500">Réelles:</span> <strong>{eq.heures_reelles?.toFixed(2).replace('.', ',')}h</strong></div>
                          <div><span className="text-slate-500">Taux moyen:</span> <strong>{formatCAD(eq.cout_moyen)}/h</strong></div>
                          <div><span className="text-slate-500">Pool peintres:</span> <strong>{formatCAD(eq.pool_peintres)}</strong></div>
                          <div><span className="text-slate-500">BT inclus:</span> <strong>{eq.nb_bons_travail || 0}</strong></div>
                        </div>

                        {/* 🆕 Chef d'équipe */}
                        {eq.chef_equipe_nom && (
                          <div className="mb-3" data-testid={`bonus-chef-${eq.chef_equipe_id}`}>
                            <h4 className="text-sm font-semibold text-slate-700 mb-2">Chef d'équipe (10%)</h4>
                            <div className="flex items-center justify-between bg-purple-50 border border-purple-100 rounded px-3 py-2 text-sm">
                              <span className="font-medium">👑 {eq.chef_equipe_nom}</span>
                              <span className="text-slate-500">{(eq.chef_heures || 0).toFixed(2).replace('.', ',')}h travaillées</span>
                              <span
                                className={`font-bold ${eq.chef_heures_economisees_perso > 0 ? 'text-emerald-700' : eq.chef_heures_economisees_perso < 0 ? 'text-red-600' : 'text-slate-500'}`}
                                data-testid={`heures-eco-perso-${eq.chef_equipe_id}`}
                              >
                                {eq.chef_heures_economisees_perso > 0 ? '+' : ''}{(eq.chef_heures_economisees_perso || 0).toFixed(2).replace('.', ',')}h
                              </span>
                              <span className="font-bold text-purple-700">{formatCAD(eq.bonus_chef_brut)}</span>
                            </div>
                          </div>
                        )}

                        {eq.bonus_par_peintre?.length > 0 && (
                          <div>
                            <h4 className="text-sm font-semibold text-slate-700 mb-2">Répartition peintres (60%)</h4>
                            <div className="space-y-1">
                              {eq.bonus_par_peintre.map(p => (
                                <div key={p.employee_id} className="flex items-center justify-between bg-slate-50 rounded px-3 py-2 text-sm" data-testid={`bonus-peintre-${p.employee_id}`}>
                                  <span className="font-medium">{p.employee_nom}</span>
                                  <span className="text-slate-500">{p.heures?.toFixed(2).replace('.', ',')}h ({p.part?.toFixed(1)}%)</span>
                                  <span
                                    className={`font-bold ${p.heures_economisees_perso > 0 ? 'text-emerald-700' : p.heures_economisees_perso < 0 ? 'text-red-600' : 'text-slate-500'}`}
                                    data-testid={`heures-eco-perso-${p.employee_id}`}
                                  >
                                    {p.heures_economisees_perso > 0 ? '+' : ''}{(p.heures_economisees_perso || 0).toFixed(2).replace('.', ',')}h
                                  </span>
                                  <span className="font-bold text-emerald-700">{formatCAD(p.bonus_brut)}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Info paliers */}
          <Card className="bg-slate-50">
            <CardHeader><CardTitle className="text-base">Barème des paliers</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[{n:'Bronze',min:0,max:50,pct:15},{n:'Argent',min:50,max:100,pct:20},{n:'Or',min:100,max:150,pct:25},{n:'Platine',min:150,max:'∞',pct:28}].map(p => (
                  <div key={p.n} className={`rounded-lg p-3 border ${(PALIER_CONFIG[p.n] || {}).bg || ''}`}>
                    <div className={`font-semibold ${(PALIER_CONFIG[p.n] || {}).color || ''}`}>{p.n}</div>
                    <div className="text-xs text-slate-500 mt-1">{p.min}h – {p.max}h économisées</div>
                    <div className="text-lg font-bold">{p.pct}%</div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
          </TabsContent>

          {/* 🆕 [Phase 55] Onglet Comparaison pondérée (Owner only) */}
          {isProprio && (
            <TabsContent value="comparaison" className="space-y-6 mt-0" data-testid="comparaison-tab-content">
              {/* 🆕 [Phase 55 FINAL] Badge d'état du flag bonus_v2_pondere */}
              {statutPondere?.active ? (
                <Alert className="bg-green-50 border-green-200" data-testid="audit-bonus-statut-actif">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                  <AlertTitle className="text-green-900 flex items-center gap-2">
                    🟢 Calcul pondéré ACTIF (Phase 55)
                  </AlertTitle>
                  <AlertDescription className="text-green-800">
                    Les bonus chefs affichés dans l&apos;onglet « Résultats par équipe » utilisent
                    désormais la répartition pondérée (au pro-rata des heures réelles via passations).
                    {statutPondere.activated_at && (
                      <span className="block text-xs mt-1 opacity-75">
                        Activé le {new Date(statutPondere.activated_at).toLocaleDateString('fr-CA')} par {statutPondere.activated_by || '—'}
                      </span>
                    )}
                  </AlertDescription>
                </Alert>
              ) : (
                <Alert className="bg-amber-50 border-amber-200" data-testid="audit-bonus-statut-shadow">
                  <Lock className="w-5 h-5 text-amber-600" />
                  <AlertTitle className="text-amber-900">🟡 Mode shadow (ancien calcul actif)</AlertTitle>
                  <AlertDescription className="text-amber-800">
                    Le calcul de bonus reste sur l&apos;ancienne méthode (100 % au chef officiel).
                    La répartition pondérée sera activée automatiquement au prochain déploiement
                    en production (migration Phase 55).
                  </AlertDescription>
                </Alert>
              )}
              {/* Bannière mode preview */}
              <Alert className="bg-blue-50 border-blue-200" data-testid="audit-bonus-preview-banner">
                <Lock className="w-5 h-5 text-blue-600" />
                <AlertTitle className="text-blue-900">Mode PREVIEW — aucune écriture en base</AlertTitle>
                <AlertDescription className="text-blue-800">
                  Comparatif côte-à-côte : bonus chef ACTUEL (100 % au chef officiel) vs POND (pro-rata
                  heures via passations <code className="bg-blue-100 px-1 rounded">chefs_par_jour</code>).
                  Aucune décision n&apos;est appliquée tant que la nouvelle répartition n&apos;est pas
                  explicitement activée (phase ultérieure).
                </AlertDescription>
              </Alert>

              {comparaisonLoading ? (
                <p className="text-center text-slate-400 py-12" data-testid="audit-bonus-loading">
                  Chargement comparaison…
                </p>
              ) : !comparaisonData ? null : (
                <>
                  {/* 4 cartes totaux */}
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <Card data-testid="audit-bonus-card-ancien">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm text-slate-500 font-normal">Total ACTUEL</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-2xl font-bold text-slate-900">{formatCAD(comparaisonData.totaux?.ancien_total)}</p>
                      </CardContent>
                    </Card>
                    <Card data-testid="audit-bonus-card-nouveau">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm text-slate-500 font-normal">Total POND simulé</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-2xl font-bold text-slate-900">{formatCAD(comparaisonData.totaux?.nouveau_total)}</p>
                      </CardContent>
                    </Card>
                    <Card data-testid="audit-bonus-card-delta">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm text-slate-500 font-normal">Delta global</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <DeltaBadge delta={comparaisonData.totaux?.delta_total || 0} />
                        <p className="text-xs text-slate-400 mt-1">≈ 0 si conservation du pool équipe</p>
                      </CardContent>
                    </Card>
                    <Card data-testid="audit-bonus-card-significatifs">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm text-slate-500 font-normal">Deltas significatifs</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-2xl font-bold text-slate-900">
                          {comparaisonData.totaux?.nb_chefs_avec_delta_significatif || 0}
                        </p>
                        <p className="text-xs text-slate-400">
                          sur {comparaisonData.totaux?.nb_chefs_concernes || 0} chefs (|Δ| &gt; 50 $)
                        </p>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Tableau */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-base">
                        <Info className="w-4 h-4 text-blue-600" />
                        Comparaison par chef d&apos;équipe — {saison}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {(comparaisonData.lignes || []).length === 0 ? (
                        <p className="text-center text-slate-400 py-8" data-testid="audit-bonus-empty">
                          Aucun chef d&apos;équipe avec bonus à afficher pour cette saison.
                        </p>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm" data-testid="audit-bonus-table">
                            <thead>
                              <tr className="text-left text-slate-500 border-b">
                                <th className="py-2 px-2">Chef</th>
                                <th className="py-2 px-2 text-right">Actuel</th>
                                <th className="py-2 px-2 text-right">POND</th>
                                <th className="py-2 px-2 text-center">Delta</th>
                                <th className="py-2 px-2 text-right">% Delta</th>
                                <th className="py-2 px-2 text-center">Jours passation</th>
                                <th className="py-2 px-2 text-center">BT impliqués</th>
                              </tr>
                            </thead>
                            <tbody>
                              {comparaisonData.lignes.map((l) => (
                                <tr
                                  key={l.chef_id}
                                  className="border-b hover:bg-slate-50"
                                  data-testid={`audit-bonus-row-${l.chef_id}`}
                                >
                                  <td className="py-2 px-2 font-medium text-slate-900">{l.chef_nom}</td>
                                  <td className="py-2 px-2 text-right text-slate-700">{formatCAD(l.ancien_bonus_chef_brut)}</td>
                                  <td className="py-2 px-2 text-right text-slate-700">{formatCAD(l.nouveau_bonus_chef_brut)}</td>
                                  <td className="py-2 px-2 text-center"><DeltaBadge delta={l.delta} /></td>
                                  <td className="py-2 px-2 text-right text-slate-500">
                                    {l.delta_pct > 0 ? '+' : ''}{l.delta_pct} %
                                  </td>
                                  <td className="py-2 px-2 text-center text-slate-600">
                                    {l.jours_passation > 0 ? <Badge variant="outline">{l.jours_passation} j</Badge> : <span className="text-slate-300">—</span>}
                                  </td>
                                  <td className="py-2 px-2 text-center text-slate-600">{l.nb_bt_concernes}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {comparaisonData.note_systeme && (
                    <p className="text-xs text-slate-400 italic" data-testid="audit-bonus-note-systeme">
                      {comparaisonData.note_systeme}
                    </p>
                  )}
                </>
              )}
            </TabsContent>
          )}
        </Tabs>
      )}
    </div>
  );
};

export default GestionBonus;
